"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "../ui";
import { CollisionPriority } from "@dnd-kit/abstract";
import { useSortable } from "@dnd-kit/react/sortable";
import { type ReactNode } from "react";
import { RiCloseLine, RiDraggable, RiFileCopyLine } from "react-icons/ri";
import { cx } from "@/lib/tailwind-utils";
import { useCarePlanContext } from "./care-plan";
import { useOptionalBranch } from "./decision/branch";
import { StepCard } from "./step-card";
import { type CarePlanStep, CarePlanStepStatus, StepType, branchContainer, collectDescendantBranchIds, findStep, findStepContainer, isPatientStep, rootContainer } from "./types";
const SORTABLE_STEP_TYPE = "care-plan-step";
interface SortableStepCardProps {
  step: CarePlanStep;
  index: number;
  children?: ReactNode;
}
export function SortableStepCard(t0) {
  const $ = _c(55);
  const {
    step,
    index,
    children
  } = t0;
  const {
    state: t1,
    actions: t2,
    config: t3,
    lookups: t4
  } = useCarePlanContext();
  const {
    steps
  } = t1;
  const {
    removeStep,
    duplicateStep,
    removeBranchStep,
    duplicateBranchStep
  } = t2;
  const {
    orientation
  } = t3;
  const {
    patientId
  } = t4;
  const restrictToSameContainer = patientId !== undefined;
  const branchCtx = useOptionalBranch();
  let t5;
  if ($[0] !== branchCtx) {
    t5 = branchCtx ? branchContainer(branchCtx.branch.id) : rootContainer();
    $[0] = branchCtx;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const containerId = t5;
  const containerBranchId = branchCtx?.branch.id;
  let t6;
  if ($[2] !== containerBranchId || $[3] !== containerId || $[4] !== restrictToSameContainer || $[5] !== steps) {
    t6 = source => {
      if (source.type !== SORTABLE_STEP_TYPE) {
        return false;
      }
      if (restrictToSameContainer) {
        const sourceContainer = findStepContainer(steps, String(source.id));
        if (sourceContainer !== containerId) {
          return false;
        }
      }
      if (!containerBranchId) {
        return true;
      }
      const sourceStep = findStep(steps, String(source.id));
      if (!sourceStep || sourceStep.type !== StepType.DECISION) {
        return true;
      }
      return !collectDescendantBranchIds(sourceStep).has(containerBranchId);
    };
    $[2] = containerBranchId;
    $[3] = containerId;
    $[4] = restrictToSameContainer;
    $[5] = steps;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      duration: 250,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      idle: false
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== containerId || $[9] !== index || $[10] !== step.id || $[11] !== t6) {
    t8 = {
      id: step.id,
      index,
      group: containerId,
      type: SORTABLE_STEP_TYPE,
      accept: t6,
      feedback: "clone",
      collisionPriority: CollisionPriority.High,
      transition: t7
    };
    $[8] = containerId;
    $[9] = index;
    $[10] = step.id;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    ref,
    handleRef,
    isDragSource
  } = useSortable(t8);
  const groupId = step.type === StepType.DECISION ? "sortabledecision" : "sortable";
  const showOnHover = step.type === StepType.DECISION ? "group-hover/sortabledecision:opacity-100" : "group-hover/sortable:opacity-100";
  const patientStep = isPatientStep(step) ? step : null;
  const isCompleted = patientStep?.status === CarePlanStepStatus.COMPLETED;
  const isLinked = !!patientStep?.eventId || !!patientStep?.taskId;
  const t9 = `group/${groupId}`;
  const t10 = orientation === "horizontal" ? "flex-row items-center" : "flex-col items-center";
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = cx("relative flex gap-2", t9, t10);
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== showOnHover) {
    t12 = cx("opacity-0 group-hover/sortable:opacity-100 size-6 text-foreground-secondary", showOnHover);
    $[16] = showOnHover;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== handleRef || $[19] !== t12) {
    t13 = <Button ref={handleRef} variant="ghost" size="icon-sm" className={t12} tooltip="Drag to reorder" icon={RiDraggable} />;
    $[18] = handleRef;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== showOnHover) {
    t14 = cx("opacity-0 group-hover/sortable:opacity-100 size-6 text-foreground-secondary", showOnHover);
    $[21] = showOnHover;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== branchCtx || $[24] !== duplicateBranchStep || $[25] !== duplicateStep || $[26] !== index || $[27] !== step) {
    t15 = e => {
      e.stopPropagation();
      if (branchCtx) {
        duplicateBranchStep(branchCtx.branch.id, step, index + 1);
      } else {
        duplicateStep(step, index + 1);
      }
    };
    $[23] = branchCtx;
    $[24] = duplicateBranchStep;
    $[25] = duplicateStep;
    $[26] = index;
    $[27] = step;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15) {
    t16 = <Button variant="ghost" size="icon-sm" className={t14} icon={RiFileCopyLine} tooltip="Duplicate step" onClick={t15} />;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== branchCtx || $[33] !== isCompleted || $[34] !== isLinked || $[35] !== removeBranchStep || $[36] !== removeStep || $[37] !== showOnHover || $[38] !== step.id) {
    t17 = !isCompleted && !isLinked && <Button variant="ghost" size="icon-sm" className={cx("opacity-0 group-hover/sortable:opacity-100 size-6 text-foreground-secondary hover:text-status-error-text", showOnHover)} icon={<RiCloseLine size={16} />} tooltip="Delete step" onClick={e_0 => {
      e_0.stopPropagation();
      if (branchCtx) {
        removeBranchStep(branchCtx.branch.id, step.id);
      } else {
        removeStep(step.id);
      }
    }} />;
    $[32] = branchCtx;
    $[33] = isCompleted;
    $[34] = isLinked;
    $[35] = removeBranchStep;
    $[36] = removeStep;
    $[37] = showOnHover;
    $[38] = step.id;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t13 || $[41] !== t16 || $[42] !== t17) {
    t18 = <div className="absolute -left-6 top-0 flex flex-col items-center gap-1">{t13}{t16}{t17}</div>;
    $[40] = t13;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== isDragSource || $[45] !== step) {
    t19 = <StepCard step={step} isDragging={isDragSource} />;
    $[44] = isDragSource;
    $[45] = step;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t18 || $[48] !== t19) {
    t20 = <div className="relative">{t18}{t19}</div>;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== children || $[51] !== ref || $[52] !== t11 || $[53] !== t20) {
    t21 = <div ref={ref} className={t11}>{t20}{children}</div>;
    $[50] = children;
    $[51] = ref;
    $[52] = t11;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
}